import { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

/* ─────────────────────────────────────────────────────────────────────
 * Lightweight redirect component for the new (Feb 2026) referral and
 * share URL shapes:
 *
 *   /ref/friend/:code       → persist attribution, hand off to /r/:code
 *                             (which has the polished invite landing)
 *   /ref/restaurant/:code   → persist attribution, hand off to
 *                             /onboarding/plan?ref=:code so the inviter
 *                             gets credited when subscription pays
 *   /share/:platform/:code  → tracking-only landing — store the
 *                             platform attribution + bounce to /r/:code
 *
 * Attribution is stored in localStorage so subsequent signups can pick
 * it up across page reloads, OAuth round-trips, and form drop-offs.
 * ─────────────────────────────────────────────────────────────────── */
const RewardsRedirect = ({ kind /* 'friend' | 'restaurant' | 'share' */ }) => {
  const { code, platform } = useParams();
  const navigate = useNavigate();

  useEffect(() => {
    const upperCode = (code || '').toUpperCase().trim();
    if (!upperCode) {
      navigate('/', { replace: true });
      return;
    }
    try {
      localStorage.setItem('nofee_referral_code', upperCode);
      localStorage.setItem('nofee_referral_type', kind);
      if (platform) localStorage.setItem('nofee_referral_platform', platform);
      localStorage.setItem('nofee_referral_at', String(Date.now()));
    } catch {
      // Private mode / quota — non-fatal, attribution simply degrades.
    }
    if (kind === 'restaurant') {
      navigate(`/onboarding/plan?ref=${upperCode}`, { replace: true });
    } else {
      // Both friend invites and social-share clicks land on the
      // existing polished /r/:code page.
      navigate(`/r/${upperCode}`, { replace: true });
    }
  }, [code, platform, kind, navigate]);

  return null;
};

export default RewardsRedirect;
